import { useEffect, useState } from 'react';
import { ReadonlySubject } from './Subject';

/**
 * Utility for subscribing to a Subject from a react component. Will ensure that
 * the state-changes in the subject causes the component to rerender.
 *
 * Example:
 *
 * const counter = new Subject<number>(0)
 * const state = counter.readonly
 *
 * setTimeout(() => {
 *   counter.update(counter.value + 1)
 * }, 1000)
 *
 * const Component = () => {
 *   const count = useSubject(state)
 *   return <div>{count}</div>
 * }
 */
export const useSubject = <T>(subject: ReadonlySubject<T>) => {
  const [state, setState] = useState(subject.value);

  useEffect(() => {
    const subscriber = (data: T) => setState(data);
    subject.subscribe(subscriber);
    return () => subject.unsubscribe(subscriber);
  }, [subject]);

  return state;
};
